// Small hand-rolled Context + reducer store — deliberately no
// external state library. It only holds state that's genuinely
// cross-cutting (balance, order history, the Telegram user);
// everything else (product lists, in-flight loading flags for a
// single page) stays local to the hook/page that owns it.

import { createContext, useContext, useReducer, useMemo } from 'react';
import type { ReactNode, Dispatch } from 'react';
import type { Order } from '@/types/order';
import type { TelegramUser } from '@/types/user';

interface AppState {
  balanceStars: number;
  orders: Order[];
  ordersLoading: boolean;
  telegramUser: TelegramUser | null;
}

type AppAction =
  | { type: 'SET_BALANCE'; payload: number }
  | { type: 'INCREMENT_BALANCE'; payload: number }
  | { type: 'SET_ORDERS'; payload: Order[] }
  | { type: 'ADD_ORDER'; payload: Order }
  | { type: 'SET_ORDERS_LOADING'; payload: boolean }
  | { type: 'SET_USER'; payload: TelegramUser | null };

const initialState: AppState = {
  balanceStars: 0,
  orders: [],
  ordersLoading: true,
  telegramUser: null,
};

function reducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'SET_BALANCE':
      return { ...state, balanceStars: action.payload };
    case 'INCREMENT_BALANCE':
      return { ...state, balanceStars: state.balanceStars + action.payload };
    case 'SET_ORDERS':
      return { ...state, orders: action.payload };
    case 'ADD_ORDER':
      return { ...state, orders: [action.payload, ...state.orders] };
    case 'SET_ORDERS_LOADING':
      return { ...state, ordersLoading: action.payload };
    case 'SET_USER':
      return { ...state, telegramUser: action.payload };
    default:
      return state;
  }
}

interface AppStoreContextValue {
  state: AppState;
  dispatch: Dispatch<AppAction>;
}

const AppStoreContext = createContext<AppStoreContextValue | null>(null);

export function AppProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const value = useMemo(() => ({ state, dispatch }), [state]);
  return <AppStoreContext.Provider value={value}>{children}</AppStoreContext.Provider>;
}

export function useAppStore(): AppStoreContextValue {
  const ctx = useContext(AppStoreContext);
  if (!ctx) {
    throw new Error('useAppStore must be used within an AppProvider');
  }
  return ctx;
}
